import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  Grid, 
  TextField, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Button, 
  Autocomplete,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GetNextTransferNo, createTransfer, updateTransfer, ApproveSolistapi, getIssues } from "../../../Services/Services.jsx";
import { CircularProgress } from "@mui/material";

const TransferForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // ✅ Mobile detection

   const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };

  const storedDivision = getSelectedDivision();
  
  // -------------------- State --------------------
  const [formData, setFormData] = useState({
    transfer_no: "",
    slip_no: "",
    from_project: "",
    from_project_name: "",
    to_project: "",
    division: storedDivision?.division_id || "", 
    created_by: localStorage.getItem("user_id"),
  });

  const [formErrors, setFormErrors] = useState({});
  const [rowErrors, setRowErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const [projectOptions, setProjectOptions] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [issues, setIssues] = useState([]);
  const isEditMode = !!location.state?.data;

  // -------------------- Utility --------------------
  const createEmptyRow = () => ({
    category: "",
    category_name: "",
    sub_category: "",
    sub_category_name: "",
    item: "",
    item_description: "",
    uom: "",
    uom_name: "",
    issue_qty: "",
    transfer_qty: "",
  });
  
  const [rows, setRows] = useState([createEmptyRow()]);

  useEffect(() => {
    fetchNextTransferNo();
    fetchProjects();
    getIssues()
      .then((res) => {
        setIssues(Array.isArray(res) ? res : res?.results);
      })
      .catch((err) => {
      });
  }, []);

  const fetchNextTransferNo = async () => {
    const data = await GetNextTransferNo();
    if (data && data.transfer_no) {
      setFormData((prev) => ({
        ...prev,
        transfer_no: data.transfer_no,
      }));
    }
  };

  // -------------------- Fetch dropdown data --------------------
   const fetchProjects = async (page = 1, filters = {}) => {
    try {
      setProjectLoading(true);
      
      const divisionData = getSelectedDivision();
      const finalFilters = divisionData?.division_id
        ? { ...filters, division: divisionData.division_id }
        : filters;
      
      const response = await ApproveSolistapi(page, finalFilters);

      const results = Array.isArray(response.results)
        ? response.results
        : response || [];

      if (page === 1) {
        setProjectOptions(results);
      } else {
        setProjectOptions((prev) => [...prev, ...results]);
      }

      setProjectPage(page);
    } catch (err) {
      toast.error("Failed to load project list");
    } finally {
      setProjectLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchProjects(1, { project_name: projectSearch });
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [projectSearch]);

  // ---------------- Populate data in edit mode --------------------
  useEffect(() => {
    if (isEditMode && issues.length) {
      const incoming = location.state.data;
      const divisionData = getSelectedDivision();

      setFormData({
        transfer_no: incoming.transfer_no || "",
        slip_no: incoming.slip_no || "",
        from_project: incoming.from_project || "",
        from_project_name: incoming.from_project_name || "",
        to_project: incoming.to_project || "",
         division: divisionData?.division_id || incoming.division || "",
        created_by: incoming.created_by || localStorage.getItem("user_id"),
      });

      const mappedItems = (incoming.items || []).map((item) => ({
        category: item.id || "",
        category_name: item.category_name || "",
        sub_category: item.subcat_id || "",
        sub_category_name: item.sub_category_name || "",
        item_description: item.item_name || "",
        uom: item.uom || "",
        uom_name: item.uom_name || "",
        issue_qty: item.issue_qty || "",
        transfer_qty: item.transfer_qty || "",
      }));
      setRows(mappedItems);
    }
  }, [isEditMode, issues, location.state]);

  const validateForm = () => {
    const errors = {};
    const rowErrs = [];

    if (!formData.slip_no) errors.slip_no = "Required";
    if (!formData.to_project) errors.to_project = "Required";

    rows.forEach((row, i) => {
      const err = {};
      if (!row.transfer_qty) err.transfer_qty = "Transfer Qty is required";
      rowErrs[i] = err;
    });

    setFormErrors(errors);
    setRowErrors(rowErrs);

    return (
      Object.keys(errors).length === 0 &&
      rowErrs.every((e) => Object.keys(e).length === 0)
    );
  };

  const handleSubmit = async () => {
    const divisionData = getSelectedDivision();
    if (!divisionData?.division_id) {
      toast.error("No division selected. Please select a division from the header.");
      return;
    }
    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        transfer_no: formData.transfer_no,
        slip_no: formData.slip_no,
        from_project: formData.from_project,
        to_project: formData.to_project,
         division: divisionData.division_id,
        created_by: formData.created_by,
        items: rows.map((row, idx) => {
          const existingItem = isEditMode ? location.state.data.items[idx] : null;
          return {
            category: row.category || existingItem?.category || "",
            sub_category: row.sub_category || existingItem?.sub_category || "",
            item: row.item || existingItem?.item || "",
            uom: row.uom || existingItem?.uom || "",
            issue_qty: row.issue_qty || existingItem?.issue_qty || "",
            transfer_qty: row.transfer_qty || "",
            issue_items: row.issue_items || existingItem?.id || "",
          };
        }),
      };

      let response;

      if (isEditMode) {
        const transfer_id = location?.state?.data?.id;
        response = await updateTransfer(transfer_id, payload);
      } else {
        response = await createTransfer(payload);
      }

      if (response) {
        toast.success(
          `Transfer ${isEditMode ? "updated" : "submitted"} successfully!`
        );
        setFormData({
          transfer_no: "",
          slip_no: "",
          from_project: "",
          from_project_name: "",
          to_project: "",
          division: storedDivision?.division_id || "",
          created_by: localStorage.getItem("user_id"),
        });
        setRows([createEmptyRow()]);
        if (isEditMode) {
          navigate("/dashboard/transferlist");
        } else {
          fetchNextTransferNo();
        }
      } else {
        toast.error(`Failed to ${isEditMode ? "update" : "submit"} transfer.`);
      }
    } catch (err) {
      toast.error("Something went wrong. Please check the form.");
    } finally {
      setLoading(false);
    }
  };

  const compactTextFieldSx = {
    "& .MuiInputBase-root": {
      height: 38,
      fontSize: "0.8rem",
    },
    "& .MuiInputBase-input": {
      padding: "4px 8px",
    },
  };

  // -------------------- JSX --------------------
  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* ✅ Action Buttons - Mobile/Desktop Responsive */}
      {isMobile ? (
        // Mobile View - Vertical Stack
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1, mb: 2, px: 1 }}>
          <Button 
            variant="outlined" 
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: 0,
              height: 35,
              fontSize: "0.7rem",
              textTransform: "none",
            }}
          >
            ← Back
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              borderRadius: 0,
              height: 35,
              fontSize: "0.7rem",
              textTransform: "none",
            }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : "Submit"}
          </Button>
        </Box>
      ) : (
        // Desktop View - Horizontal
        <Box sx={{ mt: -3, pt: 0, mb: 2, px: 1 }}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
            <Button 
              variant="outlined" 
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 0,
                height: 35,
                minWidth: 120,
                fontSize: "0.7rem",
                px: 2,
                textTransform: "none",
              }}
            >
              ← Back
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                borderRadius: 0,
                height: 35,
                minWidth: 120,
                fontSize: "0.7rem",
                px: 2,
                textTransform: "none",
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : "Submit"}
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ mb: 2, mt: { xs: 0, sm: -1 } }}>
        {/* ---- Top Filters ---- */}
        <Box 
          border="1px solid #ccc" 
          borderRadius="8px" 
          p={{ xs: 1.5, sm: 2 }} 
          bgcolor="#fff"
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {/* Transfer No */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Transfer No."
                name="transfer_no"
                value={formData.transfer_no || ""}
                disabled
                sx={{
                  "& .MuiInputBase-root": {
                    height: 38
                  }
                }}
              />
            </Grid>

            {/* Slip No Dropdown */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={issues || []}
                getOptionLabel={(option) =>
                  option.slip_no || `Slip No: ${option.id}`
                }
                value={(issues || []).find((i) => i.id === formData.slip_no) || null}
                onChange={(event, selectedSlip) => {
                  if (selectedSlip) {
                    setFormData((prev) => ({
                      ...prev,
                      slip_no: selectedSlip.id,
                      from_project: selectedSlip.project,
                      from_project_name: selectedSlip.project_name || "",
                    }));
                    
                    setFormErrors((prev) => ({ ...prev, slip_no: "" }));

                    setRows(
                      selectedSlip.items.map((item) => ({
                        category: item.category,
                        category_name: item.category_name,
                        sub_category: item.sub_category,
                        sub_category_name: item.sub_category_name,
                        item: item.item,
                        item_description: item.item_description,
                        uom: item.uom,
                        uom_name: item.uom_name,
                        issue_qty: item.issue_qty,
                        transfer_qty: "",
                        issue_items: item.id,
                        error: ""
                      }))
                    );
                  } else {
                    setFormData((prev) => ({
                      ...prev,
                      slip_no: "",
                      from_project: "",
                      from_project_name: "",
                    }));
                    setRows([createEmptyRow()]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Slip No"
                    variant="outlined"
                    required
                    error={!!formErrors.slip_no}
                    helperText={formErrors.slip_no}
                  />
                )}
              />
            </Grid>

            {/* From Project */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="From Project"
                value={formData.from_project_name || ""}
                disabled
                size="small"
                required
                error={!!formErrors.from_project}
                helperText={formErrors.from_project}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 38
                  }
                }}
              />
            </Grid>

            {/* To Project */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={projectOptions}
                getOptionLabel={(opt) => opt.project_name || ""}
                value={
                  projectOptions.find((opt) => opt.id === formData.to_project) || null
                }
                loading={projectLoading}
                onChange={(e, val) => {
                  setFormData((prev) => ({ ...prev, to_project: val?.id || "" }));

                  if (val) {
                    setFormErrors((prev) => ({ ...prev, to_project: "" }));
                  }
                }}
                onInputChange={(e, newInputValue) => {
                  setProjectSearch(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    variant="outlined"
                    label="To Project"
                    name="to_project"
                    fullWidth
                    size="small"
                    error={!!formErrors.to_project}
                    helperText={formErrors.to_project}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* ---- Table Section - Mobile/Desktop Responsive ---- */}
          <Box mt={{ xs: 2, sm: 3 }}>
            {isMobile ? (
              // ✅ Mobile Card Layout
              <Box>
                {rows.map((row, index) => (
                  <Card key={index} sx={{ mb: 2, border: "1px solid #ddd" }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: "1rem", fontWeight: 600 }}>
                        Item #{index + 1}
                      </Typography>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {/* Category */}
                        <TextField
                          fullWidth
                          label="Category"
                          value={row.category_name}
                          size="small"
                          disabled
                        />

                        {/* Subcategory */}
                        {/* <TextField
                          fullWidth
                          label="Sub Category"
                          value={row.sub_category_name}
                          size="small"
                          disabled
                        /> */}

                        {/* Item */}
                        <TextField
                          fullWidth
                          label="Item"
                          value={row.item_description}
                          size="small"
                          disabled
                        />

                        {/* UOM */}
                        <TextField
                          fullWidth
                          label="UOM"
                          value={row.uom_name}
                          size="small"
                          disabled
                        />

                        {/* Issue Qty */}
                        <TextField
                          fullWidth
                          type="number"
                          label="Issue Qty"
                          value={row.issue_qty}
                          size="small"
                          disabled
                        />

                        {/* Transfer Qty */}
                        <TextField
                          fullWidth
                          type="number"
                          label="Transfer Qty"
                          value={row.transfer_qty}
                          size="small"
                          error={!!rowErrors[index]?.transfer_qty}
                          helperText={rowErrors[index]?.transfer_qty}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            const newRows = [...rows];
                            if (value <= row.issue_qty) {
                              newRows[index].transfer_qty = value;
                            } else {
                              toast.error(
                                `Transfer Qty should not exceed Issue Qty ${row.issue_qty}`
                              );
                              newRows[index].transfer_qty = row.issue_qty;
                            }
                            setRows(newRows);

                            setRowErrors((prev) => {
                              const copy = [...prev];
                              if (copy[index]) copy[index].transfer_qty = "";
                              return copy;
                            });
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              // ✅ Desktop Table Layout
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
                      <TableCell sx={{ minWidth: 80, maxWidth: 150, textAlign: "center" }}>
                        Sl. No.
                      </TableCell>
                      <TableCell>Category</TableCell>
                      {/* <TableCell>Sub Category</TableCell> */}
                      <TableCell>Item</TableCell>
                      <TableCell>UOM</TableCell>
                      <TableCell>Issue Qty.</TableCell>
                      <TableCell>Transfer Qty</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow
                        key={index}
                        hover={false}
                        sx={{
                          "&:hover": {
                            backgroundColor: "inherit !important",
                          },
                        }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        
                        {/* Category */}
                        <TableCell>
                          <TextField
                            value={row.category_name}
                            size="small"
                            sx={{ minWidth: "180px", ...compactTextFieldSx }}
                            disabled
                          />
                        </TableCell>

                        {/* Subcategory */}
                        {/* <TableCell>
                          <TextField
                            value={row.sub_category_name}
                            size="small"
                            sx={{ minWidth: "180px", ...compactTextFieldSx }}
                            disabled
                          /> 
                        </TableCell>*/}

                        {/* Item */}
                        <TableCell>
                          <TextField
                            value={row.item_description}
                            size="small"
                            sx={{ minWidth: "180px", ...compactTextFieldSx }}
                            disabled
                          />
                        </TableCell>

                        {/* UOM */}
                        <TableCell>
                          <TextField
                            value={row.uom_name}
                            size="small"
                            sx={{ minWidth: "120px", ...compactTextFieldSx }}
                            disabled
                          />
                        </TableCell>

                        {/* Issue Qty */}
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={row.issue_qty}
                            sx={{ width: "120px", ...compactTextFieldSx }}
                            disabled
                            required
                          />
                        </TableCell>
                       
                        {/* Transfer Qty */}
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={row.transfer_qty}
                            error={!!rowErrors[index]?.transfer_qty}
                            helperText={rowErrors[index]?.transfer_qty}
                            onChange={(e) => {
                              const value = Number(e.target.value);
                              const newRows = [...rows];
                              if (value <= row.issue_qty) {
                                newRows[index].transfer_qty = value;
                              } else {
                                toast.error(
                                  `Transfer Qty should not exceed Issue Qty ${row.issue_qty}`
                                );
                                newRows[index].transfer_qty = row.issue_qty;
                              }
                              setRows(newRows);

                              setRowErrors((prev) => {
                                const copy = [...prev];
                                if (copy[index]) copy[index].transfer_qty = "";
                                return copy;
                              });
                            }}
                            sx={{ width: "120px", ...compactTextFieldSx }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default TransferForm;